import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { CheckCircle, Refresh } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { AuthModalView } from './AuthModal.tsx';
import { growSumoService } from '../../services/growSumoService.ts';

interface ConfirmSignUpFormProps {
  email: string;
  password?: string;
  firstName?: string;
  onViewChange: (view: AuthModalView) => void;
  onSuccess: () => void;
}

const ConfirmSignUpForm: React.FC<ConfirmSignUpFormProps> = ({
  email,
  password,
  firstName,
  onViewChange,
  onSuccess
}) => {
  const { confirmSignUp, resendConfirmationCode, error, isLoading, clearError } = useAuth();
  const [code, setCode] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!code.trim()) {
      setFormError('Verification code is required');
      return;
    }

    if (code.length !== 6) {
      setFormError('Verification code must be 6 digits');
      return;
    }

    clearError();
    setFormError('');

    try {
      // Pass the password to enable auto sign-in after confirmation
      await confirmSignUp(email, code, password);

      // Track email verification completion in GrowSumo
      try {
        await growSumoService.trackEvent('email_verified', {
          email: email,
          firstName: firstName
        });
      } catch (growSumoError) {
        console.warn('GrowSumo email verification tracking failed:', growSumoError);
      }

      onSuccess();
    } catch {
      // Error handled by context
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    clearError();

    try {
      await resendConfirmationCode(email);
      setResendCooldown(60);
    } catch {
      // Error handled by context
    } finally {
      setResendLoading(false);
    }
  };

  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setFormError('');
    clearError();
  };

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto' }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <CheckCircle sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Welcome{firstName ? `, ${firstName}` : ''}!
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          We've sent a 6-digit verification code to:
        </Typography>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
          {email}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Enter the code below to complete your registration and sign in.
        </Typography>
      </Box>

      {(error || formError) && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error || formError}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          label="Verification Code"
          fullWidth
          value={code}
          onChange={handleCodeChange}
          placeholder="Enter 6-digit code"
          disabled={isLoading}
          inputProps={{
            maxLength: 6,
            style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2
            }
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading || code.length !== 6}
          sx={{
            mb: 3,
            py: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Verify & Sign In'
          )}
        </Button>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Didn't receive the code?
          </Typography>
          
          <Button
            variant="text"
            onClick={handleResendCode}
            disabled={resendLoading || resendCooldown > 0}
            startIcon={resendLoading ? <CircularProgress size={16} /> : <Refresh />}
            sx={{ mb: 2 }}
          >
            {resendCooldown > 0 
              ? `Resend in ${resendCooldown}s` 
              : 'Resend Code'
            }
          </Button>

          <Box>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => onViewChange('signup')}
              sx={{ textDecoration: 'none' }}
            >
              Use a different email address
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ConfirmSignUpForm;